// socket.service.ts
import { Injectable, OnDestroy } from '@angular/core';
import { DeviceDetectorService } from 'ngx-device-detector';
import { BehaviorSubject, Observable } from 'rxjs';
import { io, Socket } from 'socket.io-client';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class SocketService implements OnDestroy {
  socket!: Socket;

  // Connection state
  private _isConnected = new BehaviorSubject<boolean>(false);
  public isConnected$: Observable<boolean> = this._isConnected.asObservable();

  // Socket info
  public socketId$ = new BehaviorSubject<string | null>(null);
  public engine$ = new BehaviorSubject<any>(null);

  // Existing subjects
  public contacts = new BehaviorSubject<any>(null);
  public session = new BehaviorSubject<any[]>([]);
  public ongoingSession = new BehaviorSubject<boolean>(false);

  // === NEW: Job feedback subjects ===
  private _jobSuccess = new BehaviorSubject<any>(null);
  public jobSuccess$: Observable<any> = this._jobSuccess.asObservable();

  private _jobFailed = new BehaviorSubject<any>(null);
  public jobFailed$: Observable<any> = this._jobFailed.asObservable();

  private _jobStatus = new BehaviorSubject<'idle' | 'pending' | 'success' | 'failed'>('idle');
  public jobStatus$: Observable<'idle' | 'pending' | 'success' | 'failed'> = this._jobStatus.asObservable();

  constructor(private deviceDetector: DeviceDetectorService) {
    this.initializeSocket();
  }

  private initializeSocket() {
    this.socket = io(environment.firewall, {
      transports: ['polling', 'websocket'],
      autoConnect: true,
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 20000,
    });

    // Connection events
    this.socket.on('connect', () => {
      console.log('Socket connected:', this.socket.id);
      this._isConnected.next(true);
      this.socketId$.next(this.socket.id!);
      this.engine$.next(this.socket.io.engine);
      this.registerClient();

      // Reset job status on reconnect
      this._jobStatus.next('idle');
    });

    this.socket.on('disconnect', (reason) => {
      console.log('Socket disconnected:', reason);
      this._isConnected.next(false);
      this.socketId$.next(null);
      this._jobStatus.next('idle');

      if (reason === 'io server disconnect') {
        this.socket.connect();
      }
    });

    this.socket.on('connect_error', (error) => {
      console.error('Socket connection error:', error);
      this._isConnected.next(false);
    });

    // === Existing custom events ===
    this.socket.on('fetchChatContacts', (args: any) => {
      this.contacts.next(args);
    });

    this.socket.on(`agentconnected-${this.socket.id}`, (data: any) => {
      const currentSession = this.session.value;
      currentSession.push(data.conversation);
      this.session.next(currentSession);
      this.ongoingSession.next(true);
    });

    // === NEW: Listen for job feedback from backend ===
    this.socket.on('job_success', (data: any) => {
      console.log('Job SUCCESS:', data);
    if(data.data.data){
      this._jobSuccess.next(data.data);
    }else{
      this._jobSuccess.next(data);
    }
      this._jobStatus.next('success');

      // Auto-reset status after 5 seconds (optional – for UI feedback)
      setTimeout(() => {
        if (this._jobStatus.value === 'success') {
          this._jobStatus.next('idle');
        }
      }, 5000);
    });

    this.socket.on('job_failed', (data: any) => {
      console.error('Job FAILED:', data);
      this._jobFailed.next(data);
      this._jobStatus.next('failed');

      // Auto-reset after 8 seconds
      setTimeout(() => {
        if (this._jobStatus.value === 'failed') {
          this._jobStatus.next('idle');
        }
      }, 8000);
    });
  }

  private registerClient() {
    const deviceInfo = this.deviceDetector.getDeviceInfo();

    this.socket.emit('register', {
      device: {
        browser: deviceInfo.browser,
        browser_version: deviceInfo.browser_version,
        os: deviceInfo.os,
        os_version: deviceInfo.os_version,
        deviceType: deviceInfo.deviceType,
        orientation: deviceInfo.orientation,
        userAgent: deviceInfo.userAgent,
      },
      app: 'afro-frontend',
    });
  }

  // Existing emit methods remain unchanged...
  public update(sock: any) { /* ... */ }
  public notifyBrowserCaptured(sock: any) { /* ... */ }
  public joinQueue(sock: any) { /* ... */ }
  public requestAuthToken(data: any) { /* ... */ }
  public authenticateWithToken(data: any) { /* ... */ }
  public onAuthTokenResponse(callback: (data: any) => void) {
    this.socket.on('request-auth-token-response', callback);
  }
  public fetchContacts() {
    this.socket.emit('fetchContactRequest', { id: '3222' });
  }

  // Utility
  public get isConnected(): boolean {
    return this._isConnected.value;
  }

  public disconnect() {
    this.socket.disconnect();
  }

  public connect() {
    this.socket.connect();
  }

  // Optional: Manual reset of job status (e.g., when starting a new transaction)
  public resetJobStatus() {
    this._jobStatus.next('idle');
    this._jobSuccess.next(null);
    this._jobFailed.next(null);
  }

  ngOnDestroy(): void {
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
    }
  }
}
